/**
 * FlowAIChat — Claude.ai-Style Chat-UI (#1180, v2.516.0).
 *
 * Layout:
 *   ┌──────────┬─────────────────────────┐
 *   │ Sessions │   Header + Messages     │
 *   │ Sidebar  │   ──────────────────    │
 *   │          │   ChatInput             │
 *   └──────────┴─────────────────────────┘
 *
 * @page /chat, /chat/:sessionId, /flowai/chat, /flowai/chat/:sessionId
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, Brain, Sparkles, Menu, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import ChatSessionsSidebar from '../../components/chat/ChatSessionsSidebar';
import UserMessage from '../../components/chat/UserMessage';
import AssistantMessage from '../../components/chat/AssistantMessage';
import ChatInput from '../../components/chat/ChatInput';
import MemoryManager from '../../components/chat/MemoryManager';
import { useChatStream } from '../../hooks/useChatStream';

const API = '';

/**
 * Wandelt DB-Messages (chat_messages rows) in Anzeige-Items:
 *   { kind: 'user', text, attachments }
 *   { kind: 'assistant', blocks: [{type, ...}], toolResults: {tool_use_id: {...}} }
 *
 * tool-Messages werden zur direkt vorhergehenden assistant-Message zugeordnet.
 */
function rowsToDisplay(rows) {
  const items = [];
  for (const r of rows || []) {
    if (r.role === 'system') continue;
    if (r.role === 'user') {
      const blocks = Array.isArray(r.content_blocks) ? r.content_blocks : null;
      if (blocks && blocks.length && blocks.some(b => b.type === 'tool_result')) {
        const lastAssistant = [...items].reverse().find(x => x.kind === 'assistant');
        if (lastAssistant) {
          for (const b of blocks) {
            if (b.type === 'tool_result') {
              lastAssistant.toolResults[b.tool_use_id] = {
                output: b.content,
                isError: !!b.is_error,
              };
            }
          }
        }
        continue;
      }
      items.push({ kind: 'user', text: r.content || '', attachments: r.attachments || [] });
      continue;
    }
    if (r.role === 'assistant') {
      const blocks = Array.isArray(r.content_blocks) && r.content_blocks.length
        ? r.content_blocks
        : (r.content ? [{ type: 'text', text: r.content }] : []);
      items.push({ kind: 'assistant', blocks, toolResults: {} });
      continue;
    }
    if (r.role === 'tool') {
      const lastAssistant = [...items].reverse().find(x => x.kind === 'assistant');
      if (lastAssistant && r.tool_result) {
        const tid = r.tool_result.tool_use_id;
        if (tid) {
          lastAssistant.toolResults[tid] = {
            output: r.tool_result.content,
            isError: !!r.tool_result.is_error,
          };
        }
      }
    }
  }
  return items;
}

export default function FlowAIChat() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();

  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(routeSessionId ? Number(routeSessionId) : null);
  const [messages, setMessages] = useState([]);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const { send, stop, streaming } = useChatStream();

  const loadSessions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/chat/sessions`, { credentials: 'include' });
      if (r.ok) setSessions(await r.json());
    } catch { /* noop */ }
  }, []);

  const loadSession = useCallback(async (id) => {
    if (!id) { setMessages([]); return; }
    try {
      const r = await fetch(`${API}/api/chat/sessions/${id}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setMessages(rowsToDisplay(data.messages || []));
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (routeSessionId) {
      const id = Number(routeSessionId);
      setActiveId(id);
      loadSession(id);
    }
  }, [routeSessionId, loadSession]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  const newSession = async () => {
    try {
      const r = await fetch(`${API}/api/chat/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: null }),
      });
      if (r.ok) {
        const s = await r.json();
        setSessions([s, ...sessions]);
        setActiveId(s.id);
        setMessages([]);
        navigate(`/chat/${s.id}`);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const selectSession = (id) => {
    setActiveId(id);
    navigate(`/chat/${id}`);
    setMobileSidebarOpen(false);
  };

  const renameSession = async (s) => {
    const newTitle = prompt(t('chat.rename') || 'Umbenennen', s.title || '');
    if (newTitle == null) return;
    const r = await fetch(`${API}/api/chat/sessions/${s.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newTitle }),
    });
    if (r.ok) loadSessions();
  };

  const archiveSession = async (s) => {
    const r = await fetch(`${API}/api/chat/sessions/${s.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived: !s.archived }),
    });
    if (r.ok) loadSessions();
  };

  const deleteSession = async (s) => {
    if (!confirm(t('chat.delete_confirm') || 'Diese Unterhaltung wirklich löschen?')) return;
    const r = await fetch(`${API}/api/chat/sessions/${s.id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (r.ok) {
      setSessions(sessions.filter(x => x.id !== s.id));
      if (activeId === s.id) {
        setActiveId(null);
        setMessages([]);
        navigate('/chat');
      }
    }
  };

  const handleSend = async (text, files) => {
    let sid = activeId;
    if (!sid) {
      const r = await fetch(`${API}/api/chat/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: text.slice(0, 60) }),
      });
      if (!r.ok) { setError('session create failed'); return; }
      const s = await r.json();
      sid = s.id;
      setActiveId(sid);
      setSessions([s, ...sessions]);
      navigate(`/chat/${sid}`);
    }

    let attachments = [];
    if (files && files.length) {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      try {
        const upRes = await fetch(`${API}/api/chat/sessions/${sid}/attachments`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        if (upRes.ok) {
          const upData = await upRes.json();
          attachments = upData.attachments || [];
        }
      } catch (e) {
        setError(e.message);
      }
    }

    setError(null);
    const userItem = {
      kind: 'user',
      text,
      attachments: attachments.length ? attachments : files.map(f => ({ filename: f.name, size: f.size })),
    };
    const assistantItem = { kind: 'assistant', blocks: [], toolResults: {}, thinking: true };
    setMessages(prev => [...prev, userItem, assistantItem]);

    await send({
      sessionId: sid,
      message: text,
      attachments,
      onEvent: (ev) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (!last || last.kind !== 'assistant') return prev;
          if (ev.type === 'text_delta') {
            const blocks = [...last.blocks];
            const tail = blocks[blocks.length - 1];
            if (tail && tail.type === 'text') {
              blocks[blocks.length - 1] = { ...tail, text: (tail.text || '') + (ev.delta || '') };
            } else {
              blocks.push({ type: 'text', text: ev.delta || '' });
            }
            copy[copy.length - 1] = { ...last, blocks, thinking: false };
          } else if (ev.type === 'tool_use') {
            const blocks = [...last.blocks, { type: 'tool_use', id: ev.id, name: ev.name, input: ev.input }];
            copy[copy.length - 1] = { ...last, blocks, thinking: false };
          } else if (ev.type === 'tool_result') {
            const toolResults = { ...last.toolResults };
            const tuId = ev.id || (last.blocks.filter(b => b.type === 'tool_use').pop() || {}).id;
            if (tuId) {
              toolResults[tuId] = {
                output: ev.output,
                durationMs: ev.durationMs,
                isError: !!ev.isError,
              };
            }
            copy[copy.length - 1] = { ...last, toolResults, thinking: false };
          } else if (ev.type === 'error') {
            setError(ev.message || 'Fehler');
            copy[copy.length - 1] = { ...last, thinking: false };
          } else if (ev.type === 'done') {
            copy[copy.length - 1] = { ...last, thinking: false };
            loadSessions();
          }
          return copy;
        });
      },
    });
  };

  const showWelcome = messages.length === 0;

  const displayMessages = useMemo(() => messages, [messages]);

  return (
    <div className="flex h-[calc(100vh-56px)] bg-surface-card">
      <div className="hidden md:flex">
        <ChatSessionsSidebar
          sessions={sessions}
          activeId={activeId}
          onSelect={selectSession}
          onNew={newSession}
          onRename={renameSession}
          onArchive={archiveSession}
          onDelete={deleteSession}
        />
      </div>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
          <div className="relative">
            <ChatSessionsSidebar
              sessions={sessions}
              activeId={activeId}
              onSelect={selectSession}
              onNew={newSession}
              onRename={renameSession}
              onArchive={archiveSession}
              onDelete={deleteSession}
            />
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="absolute top-2 right-2 p-1.5 rounded-[8px] bg-surface-card border border-line text-[var(--text-tertiary)]"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-line bg-surface-card">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden p-1.5 rounded-[8px] hover:bg-surface-hover text-[var(--text-secondary)]"
            >
              <Menu size={16} />
            </button>
            <Bot size={18} className="text-[#338edc] flex-shrink-0" />
            <div className="text-sm font-semibold text-[var(--text-primary)] truncate">
              {sessions.find(s => s.id === activeId)?.title || t('chat.title') || 'WeShip Copilot'}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMemoryOpen(true)}
              title={t('chat.memory_open') || 'Memory anzeigen'}
              className="p-1.5 rounded-[8px] hover:bg-surface-hover text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            >
              <Brain size={16} />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="max-w-3xl mx-auto">
            {showWelcome && !streaming && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-full bg-[#338edc]/10 flex items-center justify-center mb-3">
                  <Sparkles size={22} className="text-[#338edc]" />
                </div>
                <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-1">
                  {t('chat.empty_state_title') || 'WeShip Copilot'}
                </h2>
                <p className="text-sm text-[var(--text-tertiary)] max-w-md">
                  {t('chat.empty_state_desc') || 'Stelle Fragen zu Kunden, Forecasts, Cases, Mitarbeitern und mehr. Read-only Zugriff auf alle Daten.'}
                </p>
              </div>
            )}
            {displayMessages.map((m, i) => (
              m.kind === 'user'
                ? <UserMessage key={i} text={m.text} attachments={m.attachments} />
                : <AssistantMessage
                    key={i}
                    blocks={m.blocks}
                    toolResults={m.toolResults}
                    streaming={streaming && i === displayMessages.length - 1}
                    thinking={!!m.thinking}
                  />
            ))}
            {error && (
              <div className="mt-3 px-3 py-2 rounded-[10px] border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}
          </div>
        </div>

        <ChatInput
          onSend={handleSend}
          onStop={stop}
          streaming={streaming}
          disabled={false}
        />
      </div>

      <MemoryManager open={memoryOpen} onClose={() => setMemoryOpen(false)} />
    </div>
  );
}
